import AuthPageGuard from "components/AuthPageGuard";
import { Box, Flex, Heading, Spinner, Text, VStack } from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { authService } from "services/authService";
import { getThemeColor } from "theme/colors";

export default function VerifyEmail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [error, setError] = useState(
    token ? "" : t("app.auth.verifyEmail.missingToken"),
  );
  // Each link works once; StrictMode's double effect run must not spend it twice.
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;

    authService
      .verifyEmail(token)
      .then(() => navigate("/admin", { replace: true }))
      .catch((err: any) =>
        setError(
          err.response?.data?.message || t("app.auth.verifyEmail.failed"),
        ),
      );
  }, [token, navigate, t]);

  return (
    <AuthPageGuard>
      <Box w="full">
        <VStack gap={8} align="stretch">
          <Heading
            size="2xl"
            color={getThemeColor("text.primary")}
            fontWeight="bold"
          >
            {t("app.auth.verifyEmail.title")}
          </Heading>

          {error ? (
            <>
              <Box
                p={4}
                borderRadius="lg"
                bg={getThemeColor("alert.error.bg")}
                border="1px solid"
                borderColor={getThemeColor("alert.error.border")}
                color={getThemeColor("alert.error.text")}
              >
                <Text fontSize="sm" fontWeight="medium">
                  {error}
                </Text>
              </Box>
              <Flex justify="center">
                <Link to="/auth/sign-up">
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    color={getThemeColor("text.link")}
                  >
                    {t("app.auth.verifyEmail.signUpAgain")}
                  </Text>
                </Link>
              </Flex>
            </>
          ) : (
            <Flex align="center" gap={3}>
              <Spinner size="sm" />
              <Text fontSize="md" color={getThemeColor("text.secondary")}>
                {t("app.auth.verifyEmail.verifying")}
              </Text>
            </Flex>
          )}
        </VStack>
      </Box>
    </AuthPageGuard>
  );
}
